import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { detectDefaultCurrency } from '../utils/currencyUtils';

// 10 ago 2026 (pedido de Augusto: "que le informe sobre la caducidad y el
// precio, y darle la opción que pague de una vez"): antes un cliente nuevo
// no se enteraba de que tenía 14 días de prueba ni de cuánto cuesta PCM
// hasta el banner amarillo de los últimos 3 días (TrialBanner.js) o, peor,
// hasta que la prueba vencía y TrialExpiredScreen.js bloqueaba TODO sin
// aviso previo. Augusto eligió mostrarlo apenas el cliente entra por
// primera vez a /welcome (antes incluso de tocar el asistente de
// Perfil/Cuentas/Deudas), no al final del asistente ni solo reforzando el
// banner.
//
// No se muestra:
//  - a quien ya pagó o es cuenta admin (get_trial_status en deps.py
//    devuelve dias_restantes=null en esos dos casos -- ver
//    /subscription/current).
//  - a quien ya lo cerró antes en este navegador (flag en localStorage por
//    user id, mismo patrón que "onboardingCompleted"/"welcomeState" que ya
//    usa WelcomePage.js -- no hay columna en la tabla users para esto, así
//    que si el cliente cambia de navegador/dispositivo lo vuelve a ver una
//    vez; aceptable para un aviso informativo, no crítico como para
//    justificar una migración de base de datos).
const SEEN_KEY_PREFIX = 'pcm_trial_offer_seen_';

const TrialOfferModal = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const user = useSelector((state) => state.auth.user);
  const [open, setOpen] = useState(false);
  const [diasRestantes, setDiasRestantes] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const seenKey = `${SEEN_KEY_PREFIX}${user.id}`;
    if (localStorage.getItem(seenKey)) return undefined;

    let cancelled = false;
    Promise.all([
      apiClient.get('/subscription/current'),
      apiClient.get('/subscription/plans'),
    ])
      .then(([currentRes, plansRes]) => {
        if (cancelled) return;
        const { dias_restantes: dr } = currentRes.data || {};
        // null = pagó o es admin (ver get_trial_status) -- no le aplica
        // este aviso de prueba gratuita.
        if (dr == null) return;
        setDiasRestantes(dr);
        setPlans(
          Array.isArray(plansRes.data)
            ? plansRes.data.filter((p) => !p.consultar_precio)
            : []
        );
        setOpen(true);
      })
      .catch(() => {
        // Si falla la consulta, no interrumpimos con un modal a medio
        // cargar -- el banner y el bloqueo final siguen funcionando igual
        // como respaldo.
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const closeAndRemember = () => {
    if (user?.id) localStorage.setItem(`${SEEN_KEY_PREFIX}${user.id}`, '1');
    setOpen(false);
  };

  const handlePagar = () => {
    closeAndRemember();
    navigate('/subscription');
  };

  const moneda = detectDefaultCurrency(user?.country);
  const esPEN = moneda === 'PEN';

  return (
    <Dialog open={open} onClose={closeAndRemember} maxWidth="xs" fullWidth>
      <IconButton
        onClick={closeAndRemember}
        size="small"
        sx={{ position: 'absolute', right: 8, top: 8 }}
        aria-label={language === 'es' ? 'Cerrar' : 'Close'}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
        <CelebrationIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1, display: 'block', mx: 'auto' }} />
        <Typography variant="h5" fontWeight={700}>
          {language === 'es' ? '¡Bienvenido/a a PCM!' : 'Welcome to PCM!'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {language === 'es'
            ? `Tienes ${diasRestantes ?? 14} días de prueba gratis para probar todo, sin restricciones.`
            : `You have ${diasRestantes ?? 14} days of free trial to try everything, no restrictions.`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {language === 'es'
            ? 'Cuando termine, si quieres seguir usando PCM necesitarás activar un plan:'
            : "When it ends, if you want to keep using PCM you'll need to activate a plan:"}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {plans.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {p.nombre}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {esPEN ? `S/ ${Number(p.precio_pen).toFixed(2)}` : `$${Number(p.precio_usd).toFixed(2)}`}{' '}
                {language === 'es' ? '/mes' : '/mo'}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {language === 'es'
            ? 'No se te cobra nada durante la prueba. Puedes activar un plan cuando quieras.'
            : "You won't be charged during the trial. You can activate a plan whenever you want."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        <Button fullWidth variant="contained" size="large" onClick={handlePagar}>
          {language === 'es' ? 'Ver planes y pagar ahora' : 'View plans and pay now'}
        </Button>
        <Button fullWidth variant="text" onClick={closeAndRemember}>
          {language === 'es' ? 'Seguir explorando gratis' : 'Keep exploring for free'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialOfferModal;
